import type { JSX } from 'react';
import { useStore } from '@/state/store';
import { Panel, Row } from '@/components/ui/Panel';
import { assignmentColor } from '@/origami/types';
import { toDegrees } from '@/constants/numeric';

/**
 * Live analysis of the crease pattern.
 *
 * Wording rule enforced here: Kawasaki and Maekawa are *local necessary*
 * conditions. This panel never says a pattern "is flat-foldable" - deciding
 * global flat-foldability is NP-hard (Bern & Hayes 1996), and claiming otherwise
 * would be teaching the user something false.
 */
export function AnalysisPanel(): JSX.Element {
  const analysis = useStore((s) => s.analysis);
  const frame = useStore((s) => s.frame);
  const inspectVertex = useStore((s) => s.inspectVertex);
  const inspectEdge = useStore((s) => s.inspectEdge);
  const setSelection = useStore((s) => s.setSelection);
  const theme = useStore((s) => s.view.theme);

  const k = analysis;
  const kawasakiTone =
    k.kawasakiApplicable === 0 ? 'dim' : k.kawasakiPassing === k.kawasakiApplicable ? 'ok' : 'warn';
  const maekawaTone =
    k.maekawaApplicable === 0 ? 'dim' : k.maekawaPassing === k.maekawaApplicable ? 'ok' : 'warn';

  const rigid = frame?.rigid;
  const compliant = frame?.compliant;

  return (
    <>
      <Panel title="Pattern">
        <Row label="Vertices" value={k.counts.vertices} />
        <Row label="Edges" value={k.counts.edges} />
        <Row label="Faces" value={k.counts.faces} />
        <Row label="Interior vertices" value={k.interiorVertexCount} />
        <Row label="Paper area" value={k.totalArea.toFixed(5)} />
        <div className="legend" style={{ marginTop: 8 }}>
          <Legend label={`M ${k.counts.mountains}`} color={assignmentColor('M', theme)} />
          <Legend label={`V ${k.counts.valleys}`} color={assignmentColor('V', theme)} />
          <Legend label={`B ${k.counts.boundary}`} color={assignmentColor('B', theme)} />
          <Legend label={`C ${k.counts.cuts}`} color={assignmentColor('C', theme)} />
          <Legend label={`U ${k.counts.unassigned}`} color={assignmentColor('U', theme)} />
        </div>
      </Panel>

      <Panel title="Foldability analysis">
        <Row
          label="Kawasaki (local)"
          value={
            k.kawasakiApplicable === 0
              ? 'n/a'
              : `${k.kawasakiPassing} / ${k.kawasakiApplicable} vertices`
          }
          tone={kawasakiTone}
          title="Alternating sector-angle sum at each interior vertex. Necessary for flat folding, and sufficient for one vertex on its own."
        />
        <Row
          label="Maekawa (local)"
          value={
            k.maekawaApplicable === 0
              ? 'n/a'
              : `${k.maekawaPassing} / ${k.maekawaApplicable} vertices`
          }
          tone={maekawaTone}
          title="|M − V| = 2 at each interior vertex of a flat-folded pattern."
        />
        <p className="note">
          {k.kawasakiApplicable === 0
            ? 'No interior vertices, so neither theorem applies. A pattern whose creases all reach the boundary folds without any vertex condition to satisfy.'
            : k.allLocalConditionsPass
              ? 'Every interior vertex satisfies both necessary local conditions. That does not make the pattern globally flat-foldable — deciding that is NP-hard, and layers can still collide.'
              : 'At least one interior vertex fails a necessary condition, so this pattern cannot fold flat as drawn.'}
        </p>
      </Panel>

      <Panel title="Rigid foldability">
        <Row label="Fold-angle variables" value={k.dof.variables} />
        <Row label="Constraint equations" value={k.dof.constraintEquations} />
        <Row label="Independent constraints" value={k.dof.independentConstraints} />
        <Row
          label="Estimated DOF"
          value={k.dof.estimatedDof}
          tone={k.dof.estimatedDof > 0 ? 'ok' : 'warn'}
        />
        {Number.isFinite(k.dof.conditionNumber) && k.dof.conditionNumber > 0 ? (
          <Row label="Jacobian cond." value={k.dof.conditionNumber.toExponential(2)} tone="dim" />
        ) : null}
        {k.dof.notes.map((n) => (
          <p className="note" key={n}>
            {n}
          </p>
        ))}
      </Panel>

      <Panel title="Solver">
        {rigid ? (
          <>
            <Row
              label="Loop closure"
              value={`${rigid.loopClosureResidual.toExponential(2)} rad`}
              tone={rigid.converged ? 'ok' : 'err'}
            />
            <Row label="Vertex spread" value={`${rigid.vertexSpread.toExponential(2)}`} />
            <Row
              label="Edge length error"
              value={rigid.maxEdgeLengthError.toExponential(2)}
              tone={rigid.maxEdgeLengthError < 1e-9 ? 'ok' : 'warn'}
            />
            <Row label="Iterations" value={rigid.iterations} />
            <Row
              label="Target gap"
              value={`${toDegrees(rigid.maxTargetGap).toFixed(2)}°`}
              tone={rigid.maxTargetGap < 0.05 ? 'ok' : 'dim'}
            />
            {rigid.message ? <p className="note err">{rigid.message}</p> : null}
          </>
        ) : null}
        {compliant ? (
          <>
            <Row
              label="Max axial strain"
              value={`${(compliant.maxAxialStrain * 100).toFixed(3)} %`}
              tone={compliant.maxAxialStrain < 0.01 ? 'ok' : 'warn'}
            />
            <Row
              label="RMS axial strain"
              value={`${(compliant.rmsAxialStrain * 100).toFixed(3)} %`}
            />
            <Row
              label="Max crease residual"
              value={`${toDegrees(compliant.maxCreaseResidual).toFixed(2)}°`}
            />
            <Row
              label="Max facet bend"
              value={`${toDegrees(compliant.maxFacetBend).toFixed(2)}°`}
              tone={compliant.maxFacetBend < 0.15 ? 'ok' : 'warn'}
              title="How far the triangulation diagonals are from planar. Large values mean facets are bending, not just creases."
            />
            <Row label="Kinetic energy" value={compliant.kineticEnergy.toExponential(2)} />
            {compliant.diverged ? (
              <p className="note err">
                The solver produced non-finite values and was reset. Reduce the timestep or soften the
                crease stiffness.
              </p>
            ) : null}
          </>
        ) : null}
        {frame && frame.collisionCount > 0 ? (
          <p className="note warn">
            {frame.collisionCount} intersecting triangle pair(s). Contact is detected but not
            resolved, so the model shown passes through itself.
          </p>
        ) : null}
      </Panel>

      <Panel title={`Warnings (${k.issues.length})`}>
        {k.issues.length === 0 ? (
          <p className="note">No problems detected in the pattern topology.</p>
        ) : (
          k.issues.slice(0, 40).map((issue) => (
            <button
              key={issue.id}
              className={`issue ${issue.severity}`}
              onClick={() => {
                if (!issue.target) return;
                setSelection([{ kind: issue.target.kind, index: issue.target.index }]);
                if (issue.target.kind === 'vertex') inspectVertex(issue.target.index);
                if (issue.target.kind === 'edge') inspectEdge(issue.target.index);
              }}
            >
              <span className="t">{issue.title}</span>
              <span className="d">{issue.detail}</span>
            </button>
          ))
        )}
        {k.issues.length > 40 ? (
          <p className="note">…and {k.issues.length - 40} more.</p>
        ) : null}
      </Panel>
    </>
  );
}

function Legend({ label, color }: { label: string; color: string }): JSX.Element {
  return (
    <span className="chip">
      <span className="swatch" style={{ background: color }} />
      {label}
    </span>
  );
}
